import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import {
  decryptPayload,
  encryptPayload,
  isEncryptedBody,
  isEncryptionEnabled,
  isPlainUrl,
} from './payloadCrypto';
import { isSigningEnabled, signRequest } from './requestSign';

const isJsonBody = (body: unknown) =>
  body != null && typeof body === 'object' && !(body instanceof FormData);

/** Appends `params` to the url like fetchBaseQuery does, so the query is built once and signed as sent. */
const withParams = ({ url, params }: FetchArgs) => {
  const defined = Object.entries(params ?? {}).filter(([, v]) => v !== undefined);
  if (!defined.length) return url;
  const query = new URLSearchParams(Object.fromEntries(defined) as Record<string, string>);
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

/** `?page=2&q=x` -> `?payload=<aes>`. Values stay strings and repeated keys become arrays, like a plain query. */
const encryptQuery = async (url: string) => {
  const index = url.indexOf('?');
  if (index === -1) return url;
  const query: Record<string, string | string[]> = {};
  new URLSearchParams(url.slice(index + 1)).forEach((value, key) => {
    const previous = query[key];
    query[key] = previous === undefined ? value : [previous, value].flat();
  });
  if (!Object.keys(query).length) return url;
  return `${url.slice(0, index)}?payload=${encodeURIComponent(await encryptPayload(query))}`;
};

/** Path + raw query exactly as fetchBaseQuery puts it on the wire (what the HMAC signs). */
const buildSignedPath = (baseUrl: string, url: string) => {
  const u = new URL(
    `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`,
    window.location.origin
  );
  return u.href.slice(u.origin.length);
};

/**
 * Wraps any RTK Query base query with the backend's transport security:
 *  1. encrypts JSON request bodies into `{ payload }` and query strings into
 *     `?payload=` (VITE_ENABLE_ENCRYPTION)
 *  2. signs the request with HMAC headers (VITE_ENABLE_SIGNING)
 *  3. decrypts `{ payload }` responses of any status, errors included
 * Endpoints and components stay unchanged. FormData uploads are never encrypted
 * (signed with an empty body).
 *
 * `baseUrl` must match the one given to fetchBaseQuery (needed to sign the exact path).
 */
export const withPayloadEncryption =
  <E = FetchBaseQueryError>(
    baseQuery: BaseQueryFn<string | FetchArgs, unknown, E>,
    { baseUrl }: { baseUrl: string }
  ): typeof baseQuery =>
  async (args, api, extraOptions) => {
    const encrypt = isEncryptionEnabled();
    const sign = isSigningEnabled();
    if (!encrypt && !sign) return baseQuery(args, api, extraOptions);

    const request: FetchArgs = typeof args === 'string' ? { url: args } : args;
    const method = (request.method || 'GET').toUpperCase();
    const plain = isPlainUrl(request.url);

    let url = withParams(request);
    let body = request.body;
    let rawBody = '';
    const headers = new Headers(request.headers as HeadersInit | undefined);

    if (encrypt && !plain) url = await encryptQuery(url);

    if (isJsonBody(body)) {
      if (encrypt && !plain) body = { payload: await encryptPayload(body) };
      // Serialise ourselves so the signed text is exactly the text sent.
      rawBody = JSON.stringify(body);
      body = rawBody;
      headers.set('content-type', 'application/json');
    }

    if (sign) {
      const signed = await signRequest(method, buildSignedPath(baseUrl, url), rawBody);
      Object.entries(signed).forEach(([k, v]) => headers.set(k, v));
    }

    const result = await baseQuery(
      { ...request, url, params: undefined, body, headers },
      api,
      extraOptions
    );

    const error = result.error as { data?: unknown } | undefined;
    const received = error ? error.data : result.data;
    if (!encrypt || !isEncryptedBody(received)) return result;

    try {
      const data = await decryptPayload(received.payload);
      if (error) return { error: { ...error, data } as E, meta: result.meta };
      return { data, meta: result.meta };
    } catch {
      return {
        ...result,
        data: undefined,
        error: {
          status: 'PARSING_ERROR',
          originalStatus: (result.meta as FetchBaseQueryMeta | undefined)?.response?.status ?? 200,
          data: received.payload,
          error: 'Failed to decrypt response payload',
        } as unknown as E,
      };
    }
  };
